import React from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {CtaButton} from '@optimaxdev/design-system';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {AnyAction} from 'redux';

import {DoctorManualSearch} from 'components/success/contacts/doctorManualSearch/doctorManualSearch';
import {TYP_CONTACT_LENSES} from 'constants/analytics/events/typ';
import {DOCTOR_DETAILS_DEFAULT} from 'constants/product';
import {CL_RX_METHOD, VISTA_PLUS} from 'constants/success';
import {getOrderDetails, getDrawerState} from 'desktop/pages/success/selectorsV2';
import {
    changeField,
    saveOrder,
    setErrorsSuccessPage,
} from 'desktop/pages/success/store/actions/orderItems/orderItems';
import {setDrawerMethod} from 'desktop/pages/success/store/reducers/drawerPopup/drawerPopup';
import {
    getDoctorDetails,
    getSuccessPageItems,
    getSuccessPageItemsValidated,
    getValidatedItems,
} from 'desktop/pages/success/store/selectors/orderItems/orderItems';
import {getOrderLoading} from 'desktop/pages/success/store/selectors/orderStatus/orderStatus';
import {ApplicationStoreType} from 'desktop/store';

import {getLabel} from 'libs/success/helpers/helpers';
import {getPopupType} from 'libs/success/prescriptionPopup/prescriptionPopup';
import {DoctorDetailsType, SUCCESS_PAGE_FIELDS_DEFAULT} from 'types/successPage';

import {ScreenWrapper} from '../screenWrapper/screenWrapper';

import {useInitializeManualDoctorWrapper} from './hooks';

/**
 * Component for manual doctor
 */
export const ManualDoctorWrapper = () => {
    const {doctorDetails, id, isLoading, item, items, successPageItemsValidated, validatedItems} =
        useSelector(
            (store: ApplicationStoreType) => ({
                doctorDetails: getDoctorDetails(store),
                id: getOrderDetails(store).id,
                isLoading: getOrderLoading(store),
                item: getDrawerState(store).item,
                items: getSuccessPageItems(store),
                successPageItemsValidated: getSuccessPageItemsValidated(store),
                validatedItems: getValidatedItems(store),
            }),
            shallowEqual,
        );
    const dispatch = useDispatch();
    const type = getPopupType(item, '');
    const {resetError} = useInitializeManualDoctorWrapper();
    const parsedItem = validatedItems?.[item?.id || ''] as Omit<
        DoctorDetailsType,
        'filling_method' | 'location'
    >;

    const handleChangeField = (name: string, value: string) => {
        dispatch(
            changeField({
                ...SUCCESS_PAGE_FIELDS_DEFAULT,
                doctorDetails: {
                    ...DOCTOR_DETAILS_DEFAULT,
                    ...doctorDetails,
                    [name]: value,
                },
                fileType: '',
                orderItemId: String(item?.id),
                sendBy: item?.prescription?.send_by || 0,
                type: 'success',
            }),
        );
    };

    const onDoctorDetailsChange = (name: string, value: string) => {
        resetError(name);
        handleChangeField(name, value);
    };

    const handleSubmit = () => {
        const {isAllValid, items: validationItems} = successPageItemsValidated;
        sendDesktopAnalyticsEvent(
            TYP_CONTACT_LENSES,
            getLabel(CL_RX_METHOD.MANUALLY_DOCTOR, true, type),
        );

        if (isAllValid) {
            const action = saveOrder({itemId: String(item?.id), items, orderId: id});
            dispatch(action as unknown as AnyAction);
            return;
        }
        dispatch(setErrorsSuccessPage(validationItems));
        sendDesktopAnalyticsEvent(
            TYP_CONTACT_LENSES,
            `${getLabel(CL_RX_METHOD.MANUALLY_DOCTOR, false, type)} - Error-Invalid field`,
        );
    };

    const handleExamClick = () => {
        dispatch(setDrawerMethod({rxMethod: CL_RX_METHOD.EYE_EXAM}));
        sendDesktopAnalyticsEvent(
            TYP_CONTACT_LENSES,
            `${getLabel(CL_RX_METHOD.MANUALLY_DOCTOR, false, type)} - My prescription isn't valid`,
        );
    };

    return (
        <ScreenWrapper
            buttons={
                <CtaButton
                    colorName="secondary"
                    isLoading={isLoading}
                    onClick={handleSubmit}
                    size="medium"
                >
                    Confirm & Continue
                </CtaButton>
            }
        >
            <DoctorManualSearch
                doctorDetails={{...DOCTOR_DETAILS_DEFAULT, ...doctorDetails}}
                errors={parsedItem}
                handleExamClick={handleExamClick}
                isVista={item?.product?.brand?.name === VISTA_PLUS}
                onDoctorDetailsChange={onDoctorDetailsChange}
            />
        </ScreenWrapper>
    );
};
